// src/components/dashboard/ai_flow/ModernEditorToolbar.jsx
import React, { useCallback, useRef, useEffect } from "react";
import {
  Bold,
  Italic,
  AlignLeft,
  AlignCenter,
  AlignRight,
  List,
  ListOrdered,
  Link,
  Unlink,
  Undo,
  Redo,
  Type,
  Palette,
} from "lucide-react";

const ModernEditorToolbar = ({ editor, selectionKey }) => {
  const toolbarRef = useRef(null);
  const selectionRef = useRef(null);

  // Store selection whenever it updates
  useEffect(() => {
    if (editor) {
      selectionRef.current = {
        from: editor.state.selection.from,
        to: editor.state.selection.to,
      };
    }
  }, [editor, selectionKey]);

  // Execute command without losing focus or scrolling
  const executeCommand = useCallback(
    (commandFn) => {
      return (e) => {
        e.preventDefault();
        e.stopPropagation();

        // Store current scroll position
        const scrollY = window.scrollY;
        const scrollX = window.scrollX;

        // Execute command without any focus calls
        requestAnimationFrame(() => {
          if (!editor || editor.isDestroyed) return;

          commandFn();

          // Restore scroll position
          window.scrollTo(scrollX, scrollY);
        });
      };
    },
    [editor]
  );

  const formatButtons = [
    {
      icon: Bold,
      action: () => editor.chain().toggleBold().run(),
      isActive: () => editor.isActive("bold"),
      tooltip: "Bold (Ctrl+B)",
      disabled: () => !editor.can().toggleBold(),
    },
    {
      icon: Italic,
      action: () => editor.chain().toggleItalic().run(),
      isActive: () => editor.isActive("italic"),
      tooltip: "Italic (Ctrl+I)",
      disabled: () => !editor.can().toggleItalic(),
    },
  ];

  const alignmentButtons = [
    {
      icon: AlignLeft,
      action: () => editor.chain().setTextAlign("left").run(),
      isActive: () =>
        editor.isActive({ textAlign: "left" }) ||
        (!editor.isActive({ textAlign: "center" }) &&
          !editor.isActive({ textAlign: "right" })),
      tooltip: "Align Left",
    },
    {
      icon: AlignCenter,
      action: () => editor.chain().setTextAlign("center").run(),
      isActive: () => editor.isActive({ textAlign: "center" }),
      tooltip: "Align Center",
    },
    {
      icon: AlignRight,
      action: () => editor.chain().setTextAlign("right").run(),
      isActive: () => editor.isActive({ textAlign: "right" }),
      tooltip: "Align Right",
    },
  ];

  const listButtons = [
    {
      icon: List,
      action: () => editor.chain().toggleBulletList().run(),
      isActive: () => editor.isActive("bulletList"),
      tooltip: "Bullet List",
      disabled: () => !editor.can().toggleBulletList(),
    },
    {
      icon: ListOrdered,
      action: () => editor.chain().toggleOrderedList().run(),
      isActive: () => editor.isActive("orderedList"),
      tooltip: "Numbered List",
      disabled: () => !editor.can().toggleOrderedList(),
    },
  ];

  const historyButtons = [
    {
      icon: Undo,
      action: () => editor.chain().undo().run(),
      tooltip: "Undo (Ctrl+Z)",
      disabled: () => !editor.can().undo(),
    },
    {
      icon: Redo,
      action: () => editor.chain().redo().run(),
      tooltip: "Redo (Ctrl+Y)",
      disabled: () => !editor.can().redo(),
    },
  ];

  // Font size options in points (pt) for proper PDF rendering
  const fontSizeOptions = [
    { label: "8pt", value: "8pt", type: "size" },
    { label: "9pt", value: "9pt", type: "size" },
    { label: "10pt", value: "10pt", type: "size" },
    { label: "11pt", value: "11pt", type: "size" },
    { label: "12pt", value: "12pt", type: "size" },
    { label: "14pt", value: "14pt", type: "size" },
    { label: "16pt", value: "16pt", type: "size" },
    { label: "18pt", value: "18pt", type: "size" },
    { label: "20pt", value: "20pt", type: "size" },
    { label: "24pt", value: "24pt", type: "size" },
    { label: "28pt", value: "28pt", type: "size" },
  ];

  const headingOptions = [
    { label: "Normal Text", value: "p", type: "heading" },
    { label: "Heading 1", value: "h1", type: "heading" },
    { label: "Heading 3", value: "h3", type: "heading" },
  ];

  const getCurrentFontSize = () => {
    const fontSize = editor.getAttributes("textStyle").fontSize;
    return fontSize || "11pt"; // Default size in points
  };

  const setFontSize = useCallback(
    (size) => {
      // Store current scroll position
      const scrollY = window.scrollY;
      const scrollX = window.scrollX;

      requestAnimationFrame(() => {
        if (!editor || editor.isDestroyed) return;

        // Apply font size without focus
        if (size) {
          editor.commands.setFontSize(size);
        } else {
          editor.commands.unsetFontSize();
        }

        // Restore scroll position
        window.scrollTo(scrollX, scrollY);
      });
    },
    [editor]
  );

  const getHeadingLevel = () => {
    if (editor.isActive("heading", { level: 1 })) return "h1";
    if (editor.isActive("heading", { level: 3 })) return "h3";
    return "p";
  };

  const setHeadingLevel = useCallback(
    (level) => {
      // Store current scroll position
      const scrollY = window.scrollY;
      const scrollX = window.scrollX;

      requestAnimationFrame(() => {
        if (!editor || editor.isDestroyed) return;

        // Apply heading level without focus
        if (level === "h1") {
          editor.commands.setHeading({ level: 1 });
        } else if (level === "h3") {
          editor.commands.setHeading({ level: 3 });
        } else {
          editor.commands.setParagraph();
        }

        // Restore scroll position
        window.scrollTo(scrollX, scrollY);
      });
    },
    [editor]
  );

  const colorPresets = [
    { name: "Default", value: "" },
    { name: "Black", value: "#000000" },
    { name: "Dark Gray", value: "#374151" },
    { name: "Blue", value: "#1971c2" },
    { name: "Green", value: "#065f46" },
    { name: "Red", value: "#dc2626" },
  ];

  const getCurrentColor = () => {
    return editor.getAttributes("textStyle").color || "";
  };

  const setColor = useCallback(
    (color) => {
      // Store current scroll position
      const scrollY = window.scrollY;
      const scrollX = window.scrollX;

      requestAnimationFrame(() => {
        if (!editor || editor.isDestroyed) return;

        // Apply color without focus
        if (color) {
          editor.commands.setColor(color);
        } else {
          editor.commands.unsetColor();
        }

        // Restore scroll position
        window.scrollTo(scrollX, scrollY);
      });
    },
    [editor]
  );

  const setLink = useCallback(() => {
    const previousUrl = editor.getAttributes("link").href || "";
    const url = window.prompt("Enter URL", previousUrl);

    if (url === null) return;

    if (url === "") {
      editor.chain().extendMarkRange("link").unsetLink().run();
      return;
    }

    if (
      !url.startsWith("http://") &&
      !url.startsWith("https://") &&
      !url.startsWith("mailto:")
    ) {
      alert("Please enter a valid URL");
      return;
    }

    editor.chain().extendMarkRange("link").setLink({ href: url }).run();
  }, [editor]);

  // Handle dropdown changes
  const handleDropdownChange = useCallback((handler, e) => {
    e.preventDefault();
    e.stopPropagation();

    // Blur the dropdown to prevent focus issues
    e.target.blur();

    handler(e.target.value);
  }, []);

  if (!editor) return null;

  return (
    <div
      ref={toolbarRef}
      className="bg-white border-b border-gray-200 sticky top-0 z-10 no-export print:hidden"
      onMouseDown={(e) => {
        // Prevent mousedown from affecting editor focus
        if (e.target.tagName !== "SELECT") {
          e.preventDefault();
        }
      }}
    >
      <div className="flex items-center justify-between px-4 py-3">
        <div className="flex items-center space-x-1">
          {/* Text Style Dropdown */}
          <div className="relative">
            <select
              value={getHeadingLevel()}
              onChange={(e) => handleDropdownChange(setHeadingLevel, e)}
              className="appearance-none bg-gray-50 border border-gray-300 rounded-lg px-3 py-2 pr-8 text-sm font-medium text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {headingOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <Type className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          </div>

          {/* Font Size Dropdown */}
          <div className="relative">
            <select
              value={getCurrentFontSize()}
              onChange={(e) => handleDropdownChange(setFontSize, e)}
              className="appearance-none bg-gray-50 border border-gray-300 rounded-lg px-3 py-2 pr-8 text-sm font-medium text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[70px]"
            >
              {fontSizeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <div className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none text-xs font-mono">
              Aa
            </div>
          </div>

          <div className="w-px h-6 bg-gray-300 mx-2"></div>

          {/* Format Buttons */}
          <div className="flex items-center space-x-1">
            {formatButtons.map((button, index) => {
              const Icon = button.icon;
              return (
                <button
                  key={index}
                  type="button"
                  onMouseDown={executeCommand(button.action)}
                  disabled={button.disabled?.()}
                  className={`p-2 rounded-lg transition-colors ${
                    button.isActive()
                      ? "bg-blue-100 text-blue-700 border border-blue-300"
                      : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                  } ${
                    button.disabled?.() ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                  title={button.tooltip}
                  tabIndex={-1}
                >
                  <Icon className="w-4 h-4" />
                </button>
              );
            })}
          </div>

          <div className="w-px h-6 bg-gray-300 mx-2"></div>

          {/* Alignment Buttons */}
          <div className="flex items-center space-x-1">
            {alignmentButtons.map((button, index) => {
              const Icon = button.icon;
              return (
                <button
                  key={index}
                  type="button"
                  onMouseDown={executeCommand(button.action)}
                  className={`p-2 rounded-lg transition-colors ${
                    button.isActive()
                      ? "bg-blue-100 text-blue-700 border border-blue-300"
                      : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                  }`}
                  title={button.tooltip}
                  tabIndex={-1}
                >
                  <Icon className="w-4 h-4" />
                </button>
              );
            })}
          </div>

          <div className="w-px h-6 bg-gray-300 mx-2"></div>

          {/* List Buttons */}
          <div className="flex items-center space-x-1">
            {listButtons.map((button, index) => {
              const Icon = button.icon;
              return (
                <button
                  key={index}
                  type="button"
                  onMouseDown={executeCommand(button.action)}
                  disabled={button.disabled?.()}
                  className={`p-2 rounded-lg transition-colors ${
                    button.isActive()
                      ? "bg-blue-100 text-blue-700 border border-blue-300"
                      : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                  } ${
                    button.disabled?.() ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                  title={button.tooltip}
                  tabIndex={-1}
                >
                  <Icon className="w-4 h-4" />
                </button>
              );
            })}
          </div>

          <div className="w-px h-6 bg-gray-300 mx-2"></div>

          {/* Color Selector */}
          <div className="relative">
            <select
              value={getCurrentColor()}
              onChange={(e) => handleDropdownChange(setColor, e)}
              className="appearance-none bg-gray-50 border border-gray-300 rounded-lg px-3 py-2 pr-8 text-sm font-medium text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {colorPresets.map((color) => (
                <option key={color.value} value={color.value}>
                  {color.name}
                </option>
              ))}
            </select>
            <Palette className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          </div>

          <div className="w-px h-6 bg-gray-300 mx-2"></div>

          {/* Link Buttons */}
          <div className="flex items-center space-x-1">
            <button
              type="button"
              onMouseDown={executeCommand(setLink)}
              className={`p-2 rounded-lg transition-colors ${
                editor.isActive("link")
                  ? "bg-blue-100 text-blue-700 border border-blue-300"
                  : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
              }`}
              title="Insert/Edit Link"
              tabIndex={-1}
            >
              <Link className="w-4 h-4" />
            </button>
            <button
              type="button"
              onMouseDown={executeCommand(() => editor.commands.unsetLink())}
              disabled={!editor.isActive("link")}
              className={`p-2 rounded-lg transition-colors ${
                !editor.isActive("link") ? "opacity-50 cursor-not-allowed" : ""
              } text-gray-600 hover:bg-gray-100 hover:text-gray-900`}
              title="Remove Link"
              tabIndex={-1}
            >
              <Unlink className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* History Buttons */}
        <div className="flex items-center space-x-1">
          {historyButtons.map((button, index) => {
            const Icon = button.icon;
            return (
              <button
                key={index}
                type="button"
                onMouseDown={executeCommand(button.action)}
                disabled={button.disabled?.()}
                className={`p-2 rounded-lg transition-colors ${
                  button.disabled?.() ? "opacity-50 cursor-not-allowed" : ""
                } text-gray-600 hover:bg-gray-100 hover:text-gray-900`}
                title={button.tooltip}
                tabIndex={-1}
              >
                <Icon className="w-4 h-4" />
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ModernEditorToolbar;
